import { resendVerificationEmail } from '../services/auth';
import { analytics } from '../services/analytics';
import { isValidEmail } from './email-validator';

export interface ResponseData {
    type: 'success' | 'error';
    title: string;
    text: () => JSX.Element;
}

interface HandleResendVerificationEmailOptions {
    email: string;
    setResendingEmail: (value: boolean) => void;
    setResponseData: (data: ResponseData | null) => void;
    invalidEmailMessage?: string;
}

/**
 * Função compartilhada para lidar com o reenvio de email de verificação
 * Pode ser usada em qualquer componente que precise dessa funcionalidade
 */
export const handleResendVerificationEmail = async ({
    email,
    setResendingEmail,
    setResponseData,
    invalidEmailMessage = 'Por favor, informe um email válido.',
}: HandleResendVerificationEmailOptions): Promise<void> => {
    // Validar email
    if (!email || !isValidEmail(email)) {
        setResponseData({
            type: 'error',
            title: 'Email inválido',
            text: () => <p>{invalidEmailMessage}</p>
        });
        return;
    }

    analytics.addBreadcrumb('Resend verification email button clicked', 'ui', 'info');
    
    setResendingEmail(true);

    try {
        const response = await resendVerificationEmail({ email });
        
        analytics.addBreadcrumb('Verification email resent successfully', 'auth', 'info');
        
        const successData: ResponseData = {
            type: 'success',
            title: 'Email Enviado!',
            text: () => (
                <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '16px', marginBottom: '15px' }}>
                        {response.data.message}
                    </p>
                    {response.data.details && (
                        <p style={{ fontSize: '14px', color: '#666' }}>
                            {response.data.details}
                        </p>
                    )}
                </div>
            )
        };
        
        setResendingEmail(false);
        setResponseData(successData);
    } catch (error: any) {
        analytics.trackError(error, 'resend_verification_email_failed');
        
        let errorMessage = 'Não foi possível enviar o email. Tente novamente em alguns minutos.';
        
        // Tratar erro de rate limit (429)
        if (error.response?.status === 429) {
            errorMessage = 'Você atingiu o limite de reenvios. Por favor, aguarde alguns minutos e tente novamente.';
        } else if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        }
        
        const errorData: ResponseData = {
            type: 'error',
            title: 'Erro ao Enviar Email',
            text: () => <p>{errorMessage}</p>
        };
        
        setResendingEmail(false);
        setResponseData(errorData);
    }
};

